import React from 'react';
import { ArrowDownRightIcon, ArrowTopRightOnSquareIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';

const Hero: React.FC = () => {
  return (
    <div className="hero-orbit relative overflow-hidden rounded-3xl border border-fuchsia-500/40 bg-gradient-to-br from-slate-900/80 via-slate-950 to-black px-6 py-10 shadow-[0_0_90px_rgba(236,72,153,0.45)] sm:px-10 sm:py-14">
      <div className="pointer-events-none absolute -left-24 top-10 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 -bottom-10 h-72 w-72 rounded-full bg-indigo-500/15 blur-3xl" />

      <div className="relative flex flex-col gap-10 lg:flex-row lg:items-center">
        <div className="flex-1 space-y-6">
          <p className="inline-flex items-center gap-2 rounded-full border border-fuchsia-500/50 bg-fuchsia-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.25em] text-fuchsia-200/90">
            Real-time systems • Sports tech • Broadcast
          </p>
          <div>
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-slate-50 sm:text-4xl md:text-5xl">
              Architecting real-time data platforms
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-cyan-300 to-indigo-300">
                for sports and live media.
              </span>
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-[15px]">
              I&apos;m Nate Verlin, a senior software developer specializing in distributed .NET systems, AWS cloud
              infrastructure, and React/TypeScript interfaces for mission-critical, zero-downtime environments. I build
              platforms that power live golf broadcasts, sports analytics, and revenue-driving integrations.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="/Nate Verlin CV.pdf"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 shadow hover:bg-emerald-400"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Download Resume
            </a>
            <a
              href="https://www.linkedin.com/in/nate-verlin"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700/80 bg-slate-900/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100 hover:border-slate-500 hover:bg-slate-800"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              LinkedIn
            </a>
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById('experience');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-medium text-slate-300 hover:text-emerald-300"
            >
              <ArrowDownRightIcon className="h-4 w-4" />
              View experience
            </button>
          </div>

          <dl className="grid gap-4 text-xs text-slate-300 sm:grid-cols-3">
            <div>
              <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Current</dt>
              <dd className="mt-1 font-medium text-slate-100">Senior Software Developer @ PGA TOUR</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Focus</dt>
              <dd className="mt-1 font-medium text-slate-100">.NET • AWS • React • Real-time data</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Location</dt>
              <dd className="mt-1 font-medium text-slate-100">Tempe, AZ (Remote-friendly)</dd>
            </div>
          </dl>
        </div>

        <div className="flex w-full flex-col gap-4 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-5 text-xs text-slate-200 shadow-inner lg:w-72">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Profile snapshot</span>
            <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200">
              Available for select opportunities
            </span>
          </div>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span>Architected broadcast data platforms serving live golf coverage for NBC, CBS, and PGA TOUR Studios.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-cyan-400" />
              <span>Deep experience across .NET 8/10, AWS (ECS, Lambda, SQS, DynamoDB), React, TypeScript, and distributed microservices.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-indigo-400" />
              <span>Background as a Division I baseball player—comfortable operating in high-pressure, performance-critical environments.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Hero;

